import { useOIOAgent } from "./useOIOAgent"
import { useThreadContext } from "@/providers/ThreadProvider"

export type DraftStatus =
  | "draft_not_started"
  | "draft_starting"
  | "draft_running"
  | "draft_failed_terminal"
  | "draft_completed"

export function useCaseDraftStatus(): DraftStatus {
  const { state: { draft_status: agentStatus } } = useOIOAgent()
  const { activeThread } = useThreadContext()

  const meta = (activeThread?.metadata ?? {}) as Record<string, unknown>
  const metaStatus =
    typeof meta.draft_status === "string" && meta.draft_status !== ""
      ? (meta.draft_status as DraftStatus)
      : null

  // Thread metadata is the authoritative source for TERMINAL states.
  //
  // "draft_completed" and "draft_failed_terminal" are written to thread metadata
  // explicitly by the frontend when the stream ends. The LangGraph checkpoint,
  // by contrast, captures whatever the graph's last-run node wrote — which is
  // "draft_running" — because no graph node ever emits "draft_completed".
  //
  // Trusting agentStatus over metadata for these terminal values would cause the
  // spinner ("Generating OIO draft...") to appear on every page refresh even
  // when the draft finished successfully.
  if (metaStatus === "draft_completed" || metaStatus === "draft_failed_terminal") {
    return metaStatus
  }
  // A filed order is a finished draft (just additionally marked filed) — treat it
  // as completed for all draft-display logic; the "filed" flag is read separately
  // from metadata where it matters (the toolbar, the nav/vault status pill).
  if (String(meta.draft_status) === "draft_filed") {
    return "draft_completed"
  }

  // For in-progress states, the live agent state from the checkpoint is correct.
  // This covers "draft_running" and "draft_starting" during an active session.
  if (agentStatus && agentStatus !== "draft_not_started") {
    return agentStatus as DraftStatus
  }

  // Remaining pre-run metadata (e.g. "draft_starting" written before the stream
  // begins, before useCoAgent has any state to return).
  if (metaStatus && metaStatus !== "draft_not_started") {
    return metaStatus
  }

  return "draft_not_started"
}
